<script setup lang="ts">
  import { Talk } from "~/entities/Talk";
  import { useTalkRoomStore } from "~/stores/TalkRoomStore";

  interface Props {
    talkIndex: number;
    imageTalks: Talk[];
    marginClicked: boolean;
  }
  const props = defineProps<Props>();

  interface Emits {
    (event: "update:talkIndex", talkIndex: number): void;
    (event: "update:marginClicked", marginClicked: boolean): void;
  }
  const emit = defineEmits<Emits>();

  const computedTalkIndex = computed<number>({
    get: () => props.talkIndex,
    set: value => {
      emit("update:talkIndex", value);
    },
  });

  // 画像ソースを必要に応じて再読み込み
  const talkRoomStore = useTalkRoomStore();
  const talkSrcs = ref(new Array<string | undefined>(props.imageTalks.length));
  const talkSrcRatios = ref(new Array<number | undefined>(props.imageTalks.length));
  const getTalkContentsUrl = async (imageTalk: Talk) => {
    // TODO 署名付きCookieで CFn アクセスする場合はそのまま。でなければ署名付きURLを適宜更新する
    // 最終的には CFn に統一される予定

    if (!imageTalk.contentsUrl && imageTalk.fileKey) {
      const res = await talkRoomStore.getContentsUrl(imageTalk.fileKey);
      return res.presignedUrl;
    }

    if (imageTalk.contentsUrl.startsWith("https://contents.")) {
      return imageTalk.contentsUrl;
    } else {
      return await talkRoomStore.checkContentsUrl(imageTalk);
    }
  };
  const setTalkContentsUrl = async (srcIndex: number) => {
    if (talkSrcs.value[srcIndex]) {
      consoleLog("TalkImageCarousel.setTalkContentsUrl: already loaded", srcIndex, talkSrcs.value);
      return; // 既に読み込み済みの場合は処理しない
    }
    const src = await getTalkContentsUrl(props.imageTalks[srcIndex]);
    if (src) {
      talkSrcs.value[srcIndex] = src;

      // 余白計算用に画像のアスペクト比を取得
      const img = new Image();
      img.onload = () => {
        talkSrcRatios.value[srcIndex] = img.width / img.height;
      };
      img.src = src;
    }
  };
  // 初回読み込み
  setTalkContentsUrl(props.talkIndex);
  // ページ切替時に適宜読み込み
  watch(
    () => props.talkIndex,
    () => setTalkContentsUrl(props.talkIndex),
  );

  // 余白がクリックされたら親コンポーネントに通知
  const onClick = (i: number, event: PointerEvent) => {
    if (!talkSrcRatios.value[i]) {
      return; // 画像アスペクト比未取得のうちは処理しない
    }
    const imgAspectRatio = talkSrcRatios.value[i]!;
    const areaRect = (event.target! as Element).getBoundingClientRect();
    const areaAspectRatio = areaRect.width / areaRect.height;
    const x = event.x - areaRect.x;
    const y = event.y - areaRect.y;
    if (areaAspectRatio > imgAspectRatio) {
      // 左右に余白あり
      const spaceWidth = (areaRect.width * (1 - imgAspectRatio / areaAspectRatio)) / 2;
      if (x < spaceWidth || x > areaRect.width - spaceWidth) {
        emit("update:marginClicked", true);
      }
    } else {
      // 上下に余白あり
      const spaceHeight = (areaRect.height * (1 - areaAspectRatio / imgAspectRatio)) / 2;
      if (y < spaceHeight || y > areaRect.height - spaceHeight) {
        emit("update:marginClicked", true);
      }
    }
  };
</script>

<template>
  <v-carousel
    v-model="computedTalkIndex"
    hide-delimiters
    :show-arrows="false"
    :continuous="false"
    height="100vh"
    :disabled="false"
    :touch="false"
  >
    <v-carousel-item v-for="(talkSrc, i) in talkSrcs" :key="i" :src="talkSrc" contain @click="onClick(i, $event)">
      <v-progress-circular v-if="!talkSrc" indeterminate class="loading"></v-progress-circular>
    </v-carousel-item>
  </v-carousel>
</template>

<style lang="scss" scoped>
  .loading {
    position: absolute;
    left: 50%;
    top: 50%;
  }
</style>
